import { useEffect, useState } from "react";
import { RefreshCw, Link2, Unlink, Wand2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import QboConnectionVisual from "@/components/QboConnectionVisual";
import useQboConnectFlow from "@/hooks/useQboConnectFlow";
import { qboHomeLink } from "@/lib/qbo";
import { fmtDate } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

// The whole QuickBooks story in one card: OAuth connect, inbound pull,
// mapping tables (jobs/cost codes — vendors are pushed to QBO on demand,
// never mapped from it), expense-account defaults and the auto-sync
// toggle. Expense Log entries and invoices only sync from Accounting →
// Sync (Test Drive 2026-10-21); everything else follows auto-sync. Hidden entirely in export_only mode (Package 18's
// export replaces it there).
export default function QboIntegrationPanel() {
  const [status, setStatus] = useState(null);
  const [showMappings, setShowMappings] = useState(false);
  const [mappings, setMappings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [edits, setEdits] = useState({ jobs: {}, cost_codes: {} });
  const [cardAccount, setCardAccount] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [rampAccount, setRampAccount] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState("");
  const [autoSync, setAutoSync] = useState(false);
  const [syncInterval, setSyncInterval] = useState(60);
  const [busy, setBusy] = useState(false);

  const load = () => {
    return api.get("/contractor/integrations/qbo/status").then((r) => {
      setStatus(r.data);
      setCardAccount(r.data.qbo_card_account_ref || "");
      setBankAccount(r.data.qbo_bank_account_ref || "");
      setRampAccount(r.data.qbo_ramp_account_ref || "");
      setAutoSync(r.data.qbo_auto_sync || false);
      setSyncInterval(r.data.qbo_sync_interval_minutes || 60);
    }).catch(() => setStatus(null));
  };
  useEffect(() => { load(); }, []);

  const loadMappings = () => {
    api.get("/contractor/integrations/qbo/mappings").then((r) => setMappings(r.data));
    api.get("/contractor/integrations/qbo/records", { params: { type: "customer" } }).then((r) => setCustomers(r.data));
    api.get("/contractor/integrations/qbo/records", { params: { type: "account" } }).then((r) => setAccounts(r.data));
  };
  useEffect(() => { if (showMappings) loadMappings(); }, [showMappings]); // eslint-disable-line react-hooks/exhaustive-deps

  const flow = useQboConnectFlow({
    authorizePath: "/contractor/integrations/qbo/authorize",
    connected: !!status?.connected,
    reload: load,
  });

  if (!status) return null;

  const disconnect = async () => {
    await api.post("/contractor/integrations/qbo/disconnect");
    toast.success("QuickBooks disconnected");
    load();
  };

  const pullInbound = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contractor/integrations/qbo/sync-inbound");
      const p = data.pulled;
      toast.success(`Pulled ${p.customer} customers, ${p.account} accounts, ${p.class} classes, ${p.term} terms`);
      load();
      if (showMappings) loadMappings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Inbound sync failed");
    } finally {
      setBusy(false);
    }
  };

  const autoMatch = async () => {
    const { data } = await api.post("/contractor/integrations/qbo/mappings/auto");
    const m = data.auto_matched;
    toast.success(`Auto-matched ${m.jobs} jobs, ${m.cost_codes} cost codes`);
    loadMappings();
    load();
  };

  const saveMappings = async () => {
    setBusy(true);
    try {
      const toEdits = (map) => Object.entries(map).map(([id, ref]) => ({ id, ref: ref || null }));
      await api.patch("/contractor/integrations/qbo/mappings", {
        jobs: toEdits(edits.jobs), cost_codes: toEdits(edits.cost_codes),
        qbo_card_account_ref: cardAccount, qbo_bank_account_ref: bankAccount, qbo_ramp_account_ref: rampAccount, qbo_auto_sync: autoSync,
        qbo_sync_interval_minutes: Number(syncInterval),
      });
      toast.success("Mappings saved");
      setEdits({ jobs: {}, cost_codes: {} });
      loadMappings();
      load();
    } finally {
      setBusy(false);
    }
  };

  // showType (cost codes): a column with the mapped QuickBooks account's type.
  const MappingTable = ({ title, rows, kind, options, refKey, testPrefix, filter, showType }) => (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-[12px] font-bold text-slate-600">{title}</div>
        {filter}
      </div>
      {showType && rows.length > 0 && (
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-wide text-slate-400 font-semibold pb-1 border-b border-slate-100 mb-1.5">
          <div className="flex-1">Cost code</div>
          <div className="w-72">QuickBooks account</div>
          <div className="w-36">Account type</div>
        </div>
      )}
      <div className="space-y-1.5">
        {rows.map((row) => {
          const current = edits[kind][row.id] !== undefined ? edits[kind][row.id] : row[refKey] || "";
          const currentAccount = current ? accounts.find((a) => a.qbo_id === current) : null;
          return (
            <div key={row.id} className="flex items-center gap-2 text-[12.5px]">
              <div className={`flex-1 truncate ${!current ? "text-amber-700" : ""}`}>
                {row.label}
                {row.source === "quickbooks" && <span className="ml-1.5 ss-pill ss-pill-green" data-testid={`${testPrefix}-from-qbo-${row.id}`}>QuickBooks{row.category ? ` · ${row.category}` : ""}</span>}
                {!current && <span className="text-[10.5px] font-semibold ml-1.5 uppercase">unmapped</span>}
              </div>
              <SearchableSelect
                value={current}
                onChange={(e) => setEdits((m) => ({ ...m, [kind]: { ...m[kind], [row.id]: e.target.value } }))}
                data-testid={`${testPrefix}-${row.id}`}
                className={`${showType ? "w-72" : "w-56"} h-8 px-2 rounded-lg border border-slate-200 text-[12px]`}
              >
                <option value="">Not mapped</option>
                {options.map((o) => <option key={o.qbo_id} value={o.qbo_id}>{o.fully_qualified_name || o.name}{o.account_type ? ` · ${o.account_type}` : ""}</option>)}
                {current && !options.some((o) => o.qbo_id === current) && <option value={current}>{currentAccount?.fully_qualified_name || "Current account"}{currentAccount?.account_type ? ` · ${currentAccount.account_type}` : ""}</option>}
              </SearchableSelect>
              {showType && (
                <div className="w-36 truncate text-slate-500" data-testid={`${testPrefix}-type-${row.id}`}>
                  {currentAccount?.account_type ? <span className="ss-pill ss-pill-slate">{currentAccount.account_type}</span> : "—"}
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <div className="text-[12px] text-slate-400">Nothing to map.</div>}
      </div>
    </div>
  );

  // QuickBooks only accepts a Credit Card account to pay card expenses and a
  // Bank account for cash/check (anything else fails with error 6430), so
  // each picker offers only those types — and flags a saved wrong one.
  const cardAccounts = accounts.filter((a) => a.account_type === "Credit Card");
  const bankAccounts = accounts.filter((a) => a.account_type === "Bank");
  const wrongType = (ref, okTypes) => {
    const a = accounts.find((x) => x.qbo_id === ref);
    return a && a.account_type && !okTypes.includes(a.account_type) ? a : null;
  };
  // Expense lines can't post to A/P or A/R (QuickBooks rejects them).
  const lineAccounts = accounts.filter((a) => !["Accounts Payable", "Accounts Receivable"].includes(a.account_type));
  const lineAccountTypes = [...new Set(lineAccounts.map((a) => a.account_type).filter(Boolean))].sort();
  const filteredLineAccounts = accountTypeFilter ? lineAccounts.filter((a) => a.account_type === accountTypeFilter) : lineAccounts;
  const badCard = wrongType(cardAccount, ["Credit Card"]);
  const badRamp = wrongType(rampAccount, ["Credit Card"]);
  const badBank = wrongType(bankAccount, ["Bank"]);

  return (
    <div
      className={`border rounded-xl p-4 transition-all duration-700 ${flow.justConnected ? "border-emerald-400 ring-4 ring-emerald-100" : "border-slate-200"}`}
      data-testid="integration-card-qbo"
    >
      <div className="pb-4 mb-4 border-b border-slate-100">
        <QboConnectionVisual state={flow.phase} message={flow.message} />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold text-[14px]">QuickBooks Online</div>
          <div className="text-[12.5px] text-slate-500 mt-0.5">Two-way accounting sync — approved transactions post as Bills and Expenses. Expense Log entries and invoices sync only when you press Sync in Accounting.</div>
          {status.connected && (
            <div className="text-[11.5px] text-slate-400 mt-1">
              Realm {status.realm_id} · connected {status.connected_at ? fmtDate(status.connected_at) : ""}
              {" · "}{status.record_counts.customer} customers / {status.record_counts.account} accounts pulled
            </div>
          )}
          {!status.configured && (
            <div className="text-[11.5px] text-amber-700 mt-1" data-testid="qbo-not-configured">
              QuickBooks connection isn't available yet — this needs to be enabled by ScapeSource. Contact support.
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={status.connected ? "connected" : "disconnected"} />
          {status.connected ? (
            <>
              <a
                href={qboHomeLink(status.environment)} target="_blank" rel="noopener noreferrer"
                data-testid="qbo-open-link" className="text-[12px] font-semibold text-[#2CA01C] flex items-center gap-1 hover:underline"
              >
                <ExternalLink size={12} /> Open QuickBooks
              </a>
              <button onClick={pullInbound} disabled={busy} data-testid="qbo-sync-inbound-button" className="text-[12px] font-semibold text-sky-600 flex items-center gap-1 disabled:opacity-50">
                <RefreshCw size={12} /> Pull QBO Data
              </button>
              <button onClick={() => setShowMappings((v) => !v)} data-testid="qbo-mappings-toggle" className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50">
                Mappings{(status.unmapped.jobs + status.unmapped.cost_codes) > 0 ? ` (${status.unmapped.jobs + status.unmapped.cost_codes} unmapped)` : ""}
              </button>
              <button onClick={disconnect} data-testid="toggle-qbo-button" className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50 flex items-center gap-1">
                <Unlink size={12} /> Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={flow.start} disabled={!status.configured || flow.phase === "connecting"} data-testid="qbo-connect-button"
              className="px-3.5 py-2 rounded-lg bg-[#2CA01C] hover:bg-[#248217] text-white text-[12.5px] font-semibold disabled:opacity-60 flex items-center gap-1.5"
            >
              {flow.phase === "connecting"
                ? <><Loader2 size={13} className="animate-spin" /> Connecting…</>
                : <><Link2 size={13} /> Connect to QuickBooks</>}
            </button>
          )}
        </div>
      </div>

      {showMappings && status.connected && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="qbo-mappings-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[13px] font-bold">QuickBooks Mappings</div>
            <button onClick={autoMatch} data-testid="qbo-auto-match-button" className="text-[12px] font-semibold text-sky-600 flex items-center gap-1">
              <Wand2 size={12} /> Auto-match by name
            </button>
          </div>

          {mappings ? (
            <>
              <MappingTable title="Jobs → QBO Customers / Projects" rows={mappings.jobs} kind="jobs" options={customers} refKey="qbo_customer_ref" testPrefix="map-job" />
              <MappingTable
                title="Cost Codes → QBO Accounts" rows={mappings.cost_codes} kind="cost_codes" options={filteredLineAccounts} refKey="qbo_account_ref" testPrefix="map-cost-code" showType
                filter={(
                  <SearchableSelect value={accountTypeFilter} onChange={(e) => setAccountTypeFilter(e.target.value)} data-testid="map-cost-code-type-filter" className="w-48 h-8 px-2 rounded-lg border border-slate-200 text-[12px]">
                    <option value="">All account types</option>
                    {lineAccountTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </SearchableSelect>
                )}
              />

              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <div>
                  <label className="text-[12px] font-bold text-slate-600">Default credit card account (card expenses)</label>
                  <SearchableSelect value={cardAccount} onChange={(e) => setCardAccount(e.target.value)} data-testid="qbo-card-account-select" className="mt-1 w-full h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]">
                    <option value="">Not set</option>
                    {badCard && <option value={badCard.qbo_id}>{badCard.fully_qualified_name || badCard.name} (wrong type)</option>}
                    {cardAccounts.map((a) => <option key={a.qbo_id} value={a.qbo_id}>{a.fully_qualified_name || a.name}</option>)}
                  </SearchableSelect>
                  {badCard && <p className="text-[11.5px] text-red-600 mt-1" data-testid="qbo-card-account-wrong-type">"{badCard.name}" is a {badCard.account_type} account — QuickBooks rejects card expenses paid from it. Pick a Credit Card account.</p>}
                  {cardAccounts.length === 0 && <p className="text-[11.5px] text-amber-700 mt-1">No Credit Card accounts in QuickBooks yet — add one in QuickBooks (Chart of Accounts), then Pull QBO Data.</p>}
                </div>
                <div>
                  <label className="text-[12px] font-bold text-slate-600">Default bank account (cash/check expenses)</label>
                  <SearchableSelect value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} data-testid="qbo-bank-account-select" className="mt-1 w-full h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]">
                    <option value="">Not set</option>
                    {badBank && <option value={badBank.qbo_id}>{badBank.fully_qualified_name || badBank.name} (wrong type)</option>}
                    {bankAccounts.map((a) => <option key={a.qbo_id} value={a.qbo_id}>{a.fully_qualified_name || a.name}</option>)}
                  </SearchableSelect>
                  {badBank && <p className="text-[11.5px] text-red-600 mt-1" data-testid="qbo-bank-account-wrong-type">"{badBank.name}" is a {badBank.account_type} account — QuickBooks rejects cash/check expenses paid from it. Pick a Bank account.</p>}
                  {bankAccounts.length === 0 && <p className="text-[11.5px] text-amber-700 mt-1">No Bank accounts in QuickBooks yet — add one in QuickBooks, then Pull QBO Data.</p>}
                </div>
                <div>
                  <label className="text-[12px] font-bold text-slate-600">Ramp card account (Ramp charges)</label>
                  <SearchableSelect value={rampAccount} onChange={(e) => setRampAccount(e.target.value)} data-testid="qbo-ramp-account-select" className="mt-1 w-full h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]">
                    <option value="">Not set</option>
                    {badRamp && <option value={badRamp.qbo_id}>{badRamp.fully_qualified_name || badRamp.name} (wrong type)</option>}
                    {cardAccounts.map((a) => <option key={a.qbo_id} value={a.qbo_id}>{a.fully_qualified_name || a.name}</option>)}
                  </SearchableSelect>
                  <p className="text-[11.5px] text-slate-400 mt-1">Ramp card charges post here — never to the default credit card account.</p>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-4 flex-wrap">
                <label className="flex items-center gap-2 text-[12.5px] text-slate-600">
                  <input type="checkbox" checked={autoSync} onChange={(e) => setAutoSync(e.target.checked)} data-testid="qbo-auto-sync-toggle" />
                  Auto-sync in the background (pulls QuickBooks lists and pushes approved transactions)
                </label>
                {autoSync && (
                  <label className="flex items-center gap-2 text-[12.5px] text-slate-600">
                    every
                    <SearchableSelect value={syncInterval} onChange={(e) => setSyncInterval(e.target.value)} data-testid="qbo-sync-interval-select" className="h-8 px-2 rounded-lg border border-slate-200 text-[12.5px]">
                      <option value={1}>minute</option>
                      <option value={5}>5 minutes</option>
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>hour (default)</option>
                      <option value={240}>4 hours</option>
                      <option value={1440}>day</option>
                    </SearchableSelect>
                  </label>
                )}
              </div>

              <p className="text-[11.5px] text-slate-400 mt-1.5">Expense Log entries and invoices never sync automatically — send them from Accounting → Sync.</p>

              <button onClick={saveMappings} disabled={busy} data-testid="qbo-save-mappings-button" className="mt-4 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">
                Save Mappings
              </button>
            </>
          ) : (
            <div className="text-[12.5px] text-slate-400 mt-3">Loading mappings…</div>
          )}
        </div>
      )}
    </div>
  );
}
